import { ModelCell, NumberFormatSpec, StatKind } from "./tableModel.types"

// numeral-style engine hints: "0", "0,0", "0.0", "0,0.00", "0.0%", "0,0.0%"
const ENGINE_NUMBER_FORMAT = /^0(,0)?(?:\.(0+))?(%)?$/

/**
 * Parses an engine `"number"` format hint.
 * "0,0" → {0,true,false}, "0,0.0" → {1,true,false}, "0.0%" → {1,false,true}, "" → null.
 * Anything unrecognised also gives null (show the raw value).
 */
export const parseEngineNumberFormat = (
  fmt: string
): NumberFormatSpec | null => {
  if (typeof fmt !== "string") return null
  const match = ENGINE_NUMBER_FORMAT.exec(fmt.trim())
  if (!match) return null
  return {
    decimals: match[2] ? match[2].length : 0,
    thousands: Boolean(match[1]),
    percent: Boolean(match[3]),
  }
}

/** Rounds half away from zero at `decimals`, avoiding binary artefacts (1.005 → 1.01). */
const roundTo = (value: number, decimals: number): number => {
  const sign = value < 0 ? -1 : 1
  const abs = String(Math.abs(value))
  // Decimal-string shifting is exact; exponent notation (tiny/huge values) falls back.
  const shifted = abs.includes("e")
    ? Math.round(Math.abs(value) * 10 ** decimals)
    : Math.round(Number(`${abs}e${decimals}`))
  return (sign * shifted) / 10 ** decimals
}

const groupThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

/** Formats a finite number with a spec; percent specs multiply by 100. */
const formatNumber = (value: number, spec: NumberFormatSpec): string => {
  const scaled = spec.percent ? value * 100 : value
  const rounded = roundTo(scaled, spec.decimals)
  // Avoid "-0" / "-0.0" after rounding.
  const safe = rounded === 0 ? 0 : rounded
  const fixed = Math.abs(safe).toFixed(spec.decimals)
  const [intPart, fracPart] = fixed.split(".")
  const body =
    (spec.thousands ? groupThousands(intPart) : intPart) +
    (fracPart !== undefined ? `.${fracPart}` : "")
  return `${safe < 0 ? "-" : ""}${body}${spec.percent ? "%" : ""}`
}

/**
 * Formats a model cell for display.
 * - null / "" → ""; strings (letters, text, n/a cells) are shown as-is.
 * - numbers use `override[cell.kind]` when given, else the cell's own format;
 *   `decimals[cell.kind]` then replaces that format's decimals; with no
 *   format the plain number is shown.
 */
export const formatCell = (
  cell: ModelCell,
  override?: Partial<Record<StatKind, NumberFormatSpec>>,
  decimals?: Partial<Record<StatKind, number>>
): string => {
  const { raw } = cell
  if (raw === null || raw === undefined) return ""
  if (typeof raw === "string") return raw
  if (!Number.isFinite(raw)) return ""
  const spec = override?.[cell.kind] ?? cell.format
  if (!spec) return String(raw)
  const places = decimals?.[cell.kind]
  return formatNumber(raw, places === undefined ? spec : { ...spec, decimals: places })
}

/** Excel numFmt for a spec: {1,true,false} → "#,##0.0", {1,false,true} → "0.0%". */
export const toExcelNumFmt = (
  spec: NumberFormatSpec | null
): string | undefined => {
  if (!spec) return undefined
  const intPart = spec.thousands ? "#,##0" : "0"
  const fracPart = spec.decimals > 0 ? `.${"0".repeat(spec.decimals)}` : ""
  return `${intPart}${fracPart}${spec.percent ? "%" : ""}`
}
